import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import type { VersionedDeal } from 'utils/agreementVersioning'
import { formatDateString } from 'utils/formatDayjsDateTime'

interface AgreementComparisonDialogProps {
  open: boolean
  onClose: () => void
  activeVersions: VersionedDeal[]
  selectedVersion: VersionedDeal | null
  onSelect: (version: VersionedDeal) => void
  agreementFamily: string
}

const AgreementComparisonDialog = ({
  open,
  onClose,
  activeVersions,
  selectedVersion,
  onSelect,
  agreementFamily,
}: AgreementComparisonDialogProps) => {
  const theme = useTheme()

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Multiple Active {agreementFamily} Agreements Found
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Select which agreement to create the next version from.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          {activeVersions.map(version => {
            const deal = version.deal
            const isSelected = selectedVersion?.deal?.deal_uuid === deal.deal_uuid
            const isCompletedInternally = deal.noSignature || deal.dealStatus === 'Completed Internally'

            return (
              <Box
                key={deal.deal_uuid}
                onClick={() => onSelect(version)}
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : theme.palette.divider,
                  bgcolor: isSelected
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(107, 185, 240, 0.08)'
                      : 'rgba(107, 185, 240, 0.04)'
                    : 'background.paper',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: isSelected ? 'primary.main' : 'primary.light',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(107, 185, 240, 0.05)' : 'rgba(107, 185, 240, 0.02)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Chip
                    label={`v${version.semanticVersion}`}
                    size="small"
                    color={isSelected ? 'primary' : 'default'}
                    sx={{ fontFamily: 'monospace', fontWeight: 700, height: 22, fontSize: 11 }}
                  />
                  {isSelected && (
                    <Chip label="Selected" size="small" color="success" sx={{ height: 20, fontSize: 10 }} />
                  )}
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {deal.name || 'Unnamed Deal'}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <DetailRow label="Type" value={deal.type} />
                  <DetailRow label="Status" value={deal.dealStatus || deal.status || 'Active'} />
                  {isCompletedInternally && <DetailRow label="Completion" value="Completed Internally" />}
                  <DetailRow
                    label="Dates"
                    value={
                      deal.startDate || deal.endDate
                        ? `${deal.startDate ? formatDateString(deal.startDate) : '—'} to ${
                            deal.endDate ? formatDateString(deal.endDate) : '—'
                          }`
                        : 'No dates set'
                    }
                  />
                  <DetailRow label="Products" value={`${version.products.length} product(s)`} />
                  <DetailRow label="Stage" value={deal.stage || '—'} />
                </Box>
              </Box>
            )
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary" disabled={!selectedVersion}>
          Confirm Selection
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 80 }}>
      {label}:
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 500, color: value ? renderColorByString(value) : '' }}>
      {value}
    </Typography>
  </Box>
)

export default AgreementComparisonDialog
